import { useState, useRef, useCallback } from 'react';
import { Camera, Upload, X, RotateCcw, ImageIcon } from 'lucide-react';
import { Button } from './ui/button';

export function CardGuardUpload({ onUpload, uploading, label, testId, previewUrl, onRemove }) {
  const [preview, setPreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFile = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
    onUpload(file);
  }, [onUpload]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const displayUrl = previewUrl || preview;

  if (displayUrl) {
    return (
      <div className="relative group" data-testid={`${testId}-preview`}>
        <div className="relative overflow-hidden rounded-xl border-2 border-accent/30 bg-white">
          <img src={displayUrl} alt={label} className="w-full h-44 object-contain" />
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent px-3 py-2">
            <span className="text-white text-xs font-medium">{label}</span>
          </div>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={() => { setPreview(null); onRemove(); }}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`${testId}-remove`}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-xl border-2 border-dashed transition-colors ${
        dragging ? 'border-accent bg-accent/10' : 'border-border hover:border-accent hover:bg-accent/5'
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      data-testid={testId}
    >
      {/* Camera input (opens camera directly on mobile) */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFile(e.target.files[0])}
        className="hidden"
        disabled={uploading}
      />
      {/* Gallery input (opens file picker) */}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => handleFile(e.target.files[0])}
        className="hidden"
        disabled={uploading}
      />

      <div className="relative h-44 flex items-center justify-center">
        {/* Card outline */}
        <div className="absolute inset-3 border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center">
          <div className="absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-accent rounded-tl-sm" />
          <div className="absolute top-0 right-0 w-5 h-5 border-t-2 border-r-2 border-accent rounded-tr-sm" />
          <div className="absolute bottom-0 left-0 w-5 h-5 border-b-2 border-l-2 border-accent rounded-bl-sm" />
          <div className="absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-accent rounded-br-sm" />
        </div>

        <div className="flex flex-col items-center gap-3 z-10">
          {uploading ? (
            <>
              <RotateCcw className="h-6 w-6 text-accent animate-spin" />
              <span className="text-xs text-muted-foreground">アップロード中...</span>
            </>
          ) : (
            <>
              <span className="text-xs text-muted-foreground text-center px-4 leading-tight">{label}</span>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => cameraRef.current?.click()}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent text-white text-xs font-medium hover:bg-accent/90 transition-colors"
                  data-testid={`${testId}-camera`}
                >
                  <Camera className="h-4 w-4" />
                  撮影
                </button>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-secondary text-primary text-xs font-medium hover:bg-secondary/80 transition-colors border border-border"
                  data-testid={`${testId}-gallery`}
                >
                  <ImageIcon className="h-4 w-4" />
                  選択
                </button>
              </div>
              <span className="text-[10px] text-muted-foreground/40">枠内に合わせて撮影</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
